import { useContext, createContext, useMemo, useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import isEmpty from 'lodash/isEmpty';

import { CustomNotification } from 'common/components';

import { useVisibleModal } from 'hooks';
import { useValidateActiveProduct } from 'pages/home/ribbon/components/sections/product/components/make-product-active/hooks';

import * as minimumRequirementServices from 'services/minimumRequirements';
import {
  ACTIVE_PRODUCT_VALIDATE_IMAGERY_ERRORS,
  DEFAULT_TARGET_MARKET,
  DEFAULT_UNIT_COUNT,
} from 'common/components/product-add/components/constants';
import { lowercaseFirstChar } from 'hooks/useValidateSelfLoadedProduct';
import { calculateTotalFee } from 'common/components/product-add/make-product-active/utils';

export const RequiredActiveProductContext = createContext();

export const useRequiredActiveProductContext = () =>
  useContext(RequiredActiveProductContext);

export const useRequiredActiveProductModal = () => {
  const [
    visibleModalRequiredActiveProduct,
    {
      onModal: onModalRequiredActiveProduct,
      offModal: offModalRequiredActiveProduct,
    },
  ] = useVisibleModal();

  return {
    visibleModalRequiredActiveProduct,
    onModalRequiredActiveProduct,
    offModalRequiredActiveProduct,
  };
};

export const minimumRequirementsDetailKeys = ({
  entityId,
  entityName,
} = {}) => ['get-minimum-requirements', entityName, parseInt(entityId)];

export const useGetMinimumRequirementDetail = ({
  entityId,
  entityName = 'Product',
  ...otherProps
} = {}) => {
  const queryClient = useQueryClient();

  const queries = useQuery({
    queryKey: minimumRequirementsDetailKeys({
      entityId,
      entityName,
    }),
    queryFn: async () => {
      const response =
        await minimumRequirementServices.getEntityMinimumRequirementDetail({
          entityId,
          entityName,
        });

      return response?.data;
    },

    ...otherProps,
  });

  const handleValidateMinimumRequirementDetail = () => {
    queryClient.invalidateQueries({
      queryKey: minimumRequirementsDetailKeys({
        entityId,
        entityName,
      }),
    });
  };

  return {
    ...queries,
    entityRequirement: useMemo(() => queries?.data ?? {}, [queries?.data]),
    isEmptyEntityRequirement: isEmpty(queries?.data ?? {}),
    handleValidateMinimumRequirementDetail,
  };
};

export const validateMinimumRequirementKeys = ({
  requirementId,
  productId,
} = {}) => [
  'validate-minimum-requirement',
  parseInt(requirementId),
  parseInt(productId),
];

export const useValidateMinimumRequirement = ({
  requirementId,
  productId,
} = {}) => {
  const queries = useQuery({
    queryKey: validateMinimumRequirementKeys({
      requirementId,
      productId,
    }),
    queryFn: async () => {
      const response =
        await minimumRequirementServices.validateMinimumRequirement({
          id: requirementId,
          idsToCheck: [productId],
        });
      return response?.data;
    },
    enabled: Boolean(requirementId) && Boolean(productId),
    staleTime: 0,
  });

  return {
    requiredErrorFields: queries?.data?.results?.[0]?.errorFields ?? [],
    isMatched: queries?.data?.results?.[0]?.matched,
    ...queries,
  };
};

export const invalidateProductValidation = (queryClient) => {
  queryClient.invalidateQueries({
    queryKey: ['credit-card', 'validate-active-product'],
  });

  queryClient.invalidateQueries({
    queryKey: ['validate-minimum-requirement'],
  });
};

export const useUpdateUnmatchedRequirement = () => {
  return useMutation({
    mutationFn: minimumRequirementServices.updateUnmatchedRequirements,
    onSuccess: async (result) => {
      if (result?.isSuccess) {
        // CustomNotification.success('Update successfully.');
      } else {
        CustomNotification.error(result?.message || 'Something went wrong!');
      }
    },
    onError: () => {
      CustomNotification.error({ message: 'Something went wrong!' });
    },
  });
};

export const useCalculateFeeActiveProduct = ({ productIds }) => {
  const { passedProducts } = useValidateActiveProduct({
    productIds,
  });

  return calculateTotalFee(passedProducts);
};

export const useSetFormValuesAndErrors = ({
  form,
  productData,
  primaryErrors = [],
  fieldMapping = null,
  productForm,
}) => {
  useEffect(() => {
    if (isEmpty(productData) || !form) {
      return;
    }

    const productValuesFormModal = productForm
      ? productForm.getFieldsValue()
      : {};

    const defaultFieldMapping = {
      ...productData,
      ...productValuesFormModal,
      unitCount: productValuesFormModal?.unitCount || DEFAULT_UNIT_COUNT,
      targetMarket:
        productValuesFormModal?.targetMarket || DEFAULT_TARGET_MARKET,
    };

    const fieldValuesMap = fieldMapping || defaultFieldMapping;

    // Set all field values first
    form.setFieldsValue(fieldValuesMap);

    // If we have primary errors, filter and set them
    if (primaryErrors && primaryErrors.length > 0) {
      // Filter out image fields - they're handled by RequiredProductImagery component
      const nonImageErrors = filterImageErrors(primaryErrors);

      // Set errors for non-image fields that have them
      if (nonImageErrors.length > 0) {
        const fieldsWithErrors = mapErrorsToFields(
          nonImageErrors,
          fieldValuesMap
        );

        form.setFields(fieldsWithErrors);

        // ✅ CRITICAL FIX: Only validate fields that DON'T have errors from server
        // This prevents clearing the server-provided error messages
        const errorFieldNames = nonImageErrors.map((error) =>
          lowercaseFirstChar(error.fieldName)
        );

        // Get all field names from the form
        const allFieldNames = Object.keys(form.getFieldsValue());

        // Filter out fields that have errors from server
        const fieldsToValidate = allFieldNames.filter(
          (fieldName) => !errorFieldNames.includes(fieldName)
        );

        // Only validate fields without server errors
        if (fieldsToValidate.length > 0) {
          form.validateFields(fieldsToValidate).catch(() => {});
        }
      } else {
        // No non-image errors, validate all fields normally
        form.validateFields().catch(() => {});
      }
    } else {
      // No errors from server, validate all fields normally
      form.validateFields().catch(() => {});
    }
  }, [form, productData, primaryErrors, fieldMapping, productForm]);
};

/**
 * Filters out image field errors from primary errors
 * Image errors are handled separately by RequiredProductImagery component
 *
 * @param {Array} primaryErrors - Array of error objects
 * @returns {Array} Filtered array without image field errors
 */
const filterImageErrors = (primaryErrors) => {
  const imageFieldIds = Object.keys(ACTIVE_PRODUCT_VALIDATE_IMAGERY_ERRORS).map(
    (key) => key.toLowerCase()
  );

  return primaryErrors.filter(
    (error) => !imageFieldIds.includes(error?.fieldName?.toLowerCase())
  );
};

/**
 * Maps error objects to Ant Design form field format
 *
 * @param {Array} errors - Array of error objects with fieldName and errorMessage
 * @param {Object} fieldValuesMap - Map of field names to values
 * @returns {Array} Array of field objects for setFields()
 */
const mapErrorsToFields = (errors, fieldValuesMap) => {
  return errors.map((error) => {
    const fieldName = lowercaseFirstChar(error.fieldName);
    const fieldValue = fieldValuesMap[fieldName];

    // Build field object
    const fieldObject = {
      name: fieldName,
      errors: [error.errorMessage],
      touched: true,
      validating: false, // Prevent validation from overriding these errors
    };

    // Only include 'value' if field exists in fieldValuesMap
    if (fieldName in fieldValuesMap) {
      fieldObject.value = fieldValue;
    }

    return fieldObject;
  });
};
